

import React, { useEffect, useState } from "react";
import 'react-circular-progressbar/dist/styles.css';
import { CSVLink } from 'react-csv'; // Import CSVLink from react-csv
import { toast } from "react-toastify";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import BrandHeader from "./BrandHeader";
import Navbar from "./Navbar";
// Import MUI Icons
import BarChartIcon from '@mui/icons-material/BarChart';
import CampaignIcon from '@mui/icons-material/Campaign';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import PeopleIcon from '@mui/icons-material/People';
import PublicIcon from '@mui/icons-material/Public';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link } from "react-router-dom";

const InfluencerHistory = () => {
  const [myCampaigns, setMyCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [campaignData, setCampaignData] = useState(null);
  const [selectedCampaignName, setSelectedCampaignName] = useState("");  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const storedBrandId = localStorage.getItem("brandID");
    if (storedBrandId) {
      fetchCampaigns(storedBrandId);
    } else {
      toast.error("No brand ID found in localStorage!");
    }
  }, []);

  const fetchCampaigns = async (brandId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://server-side-influencer.onrender.com/brand/getMyCampaigns/${brandId}`,
        { method: "GET", headers: { "Content-Type": "application/json" } }
      );
      const data = await response.json();
      if (data.success) {
        setMyCampaigns(data.data);
        setFilteredCampaigns(data.data);
      } else {
        toast.error(data.message || "Failed to fetch campaigns");
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error("Error fetching campaigns");
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaignAnalytics = (campaignId) => {
    fetch(`https://server-side-influencer.onrender.com/brand/getCampaignAnalytics/${campaignId}`)
      .then((response) => response.json())
      .then((data) => setCampaignData(data.data))
      .catch((error) => console.error('Error fetching campaign analytics:', error));
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSelectedCampaignName(query);
    const filteredCampaigns = myCampaigns.filter(campaign =>
      campaign.campaignName.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCampaigns(filteredCampaigns);
  };

  const handleCampaignSelect = (campaignId) => {
    setSelectedCampaignName(myCampaigns.find(campaign => campaign._id === campaignId)?.campaignName || "");
    fetchCampaignAnalytics(campaignId);
    setFilteredCampaigns([]); // Hide suggestions after a campaign is selected
  };

  // Fallback data and rendering
  const fallbackData = [
    { date: "2025-02-01", totalApplicants: 0, totalReach: 0, selectedUsers: 0 },
    { date: "2025-02-02", totalApplicants: 0, totalReach: 0, selectedUsers: 0 }
  ];

  const dataToRender = campaignData?.dayWiseAnalytics?.map((entry) => ({
    ...entry,
    selectedUsers: entry.usersSelectedForCampaign || 0,
    date: new Date(entry.date).toLocaleDateString(), // Format dates
  })) || fallbackData;

  // CSV export data
  const csvData = campaignData?.dayWiseAnalytics?.map(entry => ({
    Date: new Date(entry.date).toLocaleDateString(),
    TotalApplicants: entry.totalApplicants || 0,
    TotalReach: entry.totalReach || 0,
    SelectedUsers: entry.usersSelectedForCampaign || 0,
  })) || [];

  // CSV headers
  const csvHeaders = [
    { label: "Date", key: "Date" },
    { label: "Total Applicants", key: "TotalApplicants" },
    { label: "Total Reach", key: "TotalReach" },
    { label: "Selected Users", key: "SelectedUsers" }
  ];

  return (
    <div className="flex h-screen mb-0 justify-center items-center w-full">
      <div className="text-white fixed top-0 left-0 bottom-0 z-50">
        <Navbar />
      </div>

      <div className="h-screen ml-0 w-full">
        <BrandHeader page="Brand Profile" />
        <div className="h-screen ml-19 sm:ml-0 w-screen p-8 bg-white mb-10">
          <div className="text-center font-bold text-3xl mt-4">Campaign Analytics</div>
          <div className="text-center text-gray-600 mt-2 mb-10">
            View your campaign data in real-time and analyze performance.

            <div className="mb-4 flex justify-center mt-10">
              <div className="relative w-full max-w-md">
                <input
                  type="text"
                  value={selectedCampaignName}
                  onChange={handleSearchChange}
                  placeholder="Search by campaign name"
                  className="w-full px-4 py-2 pl-10 border rounded-md  bg-white border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                  <i className="fas fa-search"></i>
                </span>

                {/* Hide Campaign Suggestions after selection */}
                {selectedCampaignName && filteredCampaigns.length > 0 && (
                  <div className="absolute w-full max-w-md mt-1 bg-white shadow-lg rounded-md border border-gray-300 z-10 mb-12">
                    <ul>
                      {filteredCampaigns.map((campaign) => (
                        <li
                          key={campaign._id}
                          onClick={() => handleCampaignSelect(campaign._id)}
                          className="cursor-pointer p-2 hover:bg-gray-200"
                        >
                          {campaign.campaignName}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Message if no campaigns found */}
                {selectedCampaignName && filteredCampaigns.length === 0 && (
                  <p className="text-center mt-2 text-gray-500">No campaigns found</p>
                )}
              </div>
            </div>
          </div>

          {/* Export CSV Button */}
          <div style={{ position: 'absolute', top: '70px', right: '60px' }}>
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename={`campaign_analytics_${new Date().toLocaleDateString()}.csv`}
              className="text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
            >
              Export to CSV
            </CSVLink>
          </div>

          {/* Analytics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8 ml-12" style={{marginTop:"70px"}}>
            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <PublicIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Total Reach</div>
                  <div className="text-lg">{campaignData ? campaignData.totalReach : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <CampaignIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Active Campaigns</div>
                  <div className="text-lg">{campaignData ? campaignData.activeCampaigns : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <PeopleIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Total Applicants</div>
                  <div className="text-lg">{campaignData ? campaignData.totalApplicants : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <PeopleIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Total Spend</div>
                  <div className="text-lg">₹{campaignData ? campaignData.totalSpend : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <MoneyOffIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">CPM</div>
                  <div className="text-lg">{campaignData ? campaignData.cpm : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <PublicIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">CTR</div>
                  <div className="text-lg">{campaignData ? campaignData.ctr : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <BarChartIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Conversion Rate</div>
                  <div className="text-lg">{campaignData ? campaignData.conversionRate : 0}%</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <VisibilityIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Total Impressions</div>
                  <div className="text-lg">{campaignData ? campaignData.totalImpressions : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-600 to-green-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <ThumbUpIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Total Likes</div>
                  <div className="text-lg">{campaignData ? campaignData.totalLikes : 0}</div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-600 to-yellow-500 p-6 rounded-xl shadow-lg text-white">
              <div className="flex items-center">
                <PeopleIcon className="text-3xl mr-4" />
                <div>
                  <div className="text-2xl font-bold">Selected Users</div>
                  <div className="text-lg">{campaignData ? campaignData.usersSelectedForCampaign : 0}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Campaign Analytics Chart ss*/}
          {campaignData && (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={dataToRender}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="totalApplicants" stroke="#8884d8" />
                <Line type="monotone" dataKey="totalReach" stroke="#82ca9d" />
                <Line type="monotone" dataKey="selectedUsers" stroke="#ff7300" />
              </LineChart>
            </ResponsiveContainer>
          )}

       

          {/* Applicant Details Tables*/}
          {campaignData?.applicants && campaignData.applicants.length > 0 && (
            <div style={{ maxHeight: "400px", overflowY: "auto", marginTop: '2rem' }}>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', padding: '1rem', textAlign: 'center' }}>
                Applicant Details
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full table-auto border-collapse bg-white text-gray-700" style={{ marginLeft: '60px' }}>
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-4 py-2 text-left">Profilepic</th>
                      <th className="border px-4 py-2 text-left">Name</th>
                      <th className="border px-4 py-2 text-left">Age</th>
                      <th className="border px-4 py-2 text-left">Followers</th>
                      <th className="border px-4 py-2 text-left">Email</th>
                      <th className="border px-4 py-2 text-left">Status</th>
                      <th className="border px-4 py-2 text-left">Approved Budget</th>
                      <th className="border px-4 py-2 text-left">Instagram</th>
                      <th className="border px-4 py-2 text-left">Post</th>
                      <th className="border px-4 py-2 text-left">Reels</th>
                      <th className="border px-4 py-2 text-left">Task Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {campaignData.applicants.map((applicant) => (
                      <tr key={applicant._id} className="hover:bg-gray-50">
                        <td className="border px-4 py-2">
                          <img src={applicant.profilePic} alt={applicant.name} className="w-10 h-10 rounded-full object-cover ml-5" />
                        </td>
                        <td className="border px-4 py-2">{applicant.name}</td>
                        <td className="border px-4 py-2">{applicant.age}</td>
                        <td className="border px-4 py-2">{applicant.followers}</td>
                        <td className="border px-4 py-2">
                          <a href={`mailto:${applicant.email}`} className="flex items-center text-blue-500 hover:text-blue-700">
                            <EmailIcon style={{ marginRight: "5px" }} />
                            {applicant.email}
                          </a>
                        </td>
                        <td className="border px-4 py-2">{applicant.status}</td>
                        <td className="border px-4 py-2">₹{applicant.approvedBudget}</td>
                        <td className="border px-4 py-2">
                          <a
                            href={applicant.instagramLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-blue-500 hover:text-blue-700"
                          >
                            <InstagramIcon className="mr-2" />
                            Instagram
                          </a>
                        </td>
                        <td className="border px-4 py-2">
                          <Link
                            to=""
                            className="text-blue-500 hover:text-blue-700"
                          >
                            {applicant.postCount || 0}
                          </Link>
                        </td>
                        <td className="border px-4 py-2">{applicant.reelCount || 0}</td>
                        <td className="border px-4 py-2">
                          <a
                            href={applicant.taskLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700"
                          >
                            Task Link
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default InfluencerHistory;
